import { Fragment, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, ExternalLink, BellOff, Check, Sparkles } from 'lucide-react'
import type {
  ReviewChaseCheckoutTodayRow,
  ReviewChasePendingRow,
  ReviewChaseSkippedRow,
} from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { SuggestedReviewBlock } from '@/components/review-chase/suggested-review-block'
import { ConfidenceToggle, ConfidenceRow } from '@/components/review-chase/confidence-toggle'
import { ThankYouMessageDialog } from '@/components/review-chase/thank-you-message-dialog'
import { RemindDialog, type RemindTarget } from '@/components/review-chase/remind-dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { useToast } from '@/hooks/use-toast'
import { REVIEW_ROW_PARAM } from '@/lib/review-events'
import { cn } from '@/lib/utils'

// Thin, form-local schema for the skip dialog's optional reason textarea
// (not the raw contracts-package schema — this form's only input is the
// free-text reason). No min-length rule: unlike the other single-textarea
// forms in this migration, a skip reason is genuinely optional (see
// onSubmitSkip's own `|| undefined` below).
const skipReasonSchema = z.object({
  text: z.string(),
})
type SkipReasonValues = z.infer<typeof skipReasonSchema>

// SuggestedReviewBlock/ThankYouMessageDialog don't export their prop
// interfaces, only the components themselves — derive the real prop types
// from the component signatures instead of re-declaring (and risking
// drifting from) their shapes here.
type SuggestedReviewBlockProps = Parameters<typeof SuggestedReviewBlock>[0]
// The digest's embedded per-row suggestion, in whatever shape
// SuggestedReviewBlock accepts as its initialSuggestion prop.
type ReviewSuggestionEmbed = SuggestedReviewBlockProps['initialSuggestion']

// The admin-api response actually embeds a `reviewSuggestion` field on each
// digest row (see SuggestedReviewBlock's doc comment: proactively generated
// by the review-window-open webhook), but the ReviewChase*RowSchemas in
// @natalan/capehost-contracts don't model it — a real contract/schema gap
// in the published package. client.ts's getReviewChaseDigest patches around
// it locally (ReviewChaseDigestResponseWithSuggestionsSchema) so runtime
// validation doesn't strip the field; the upstream schema still needs it
// added for real.
type DigestRow = (ReviewChaseCheckoutTodayRow | ReviewChasePendingRow) & {
  daysSince?: number
  tier?: string
  // Pending rows carry it from the contract; checkout rows only when a chase
  // already exists for the stay (null otherwise).
  externalId?: string | null
  reviewSuggestion?: ReviewSuggestionEmbed
  // CAP-301: the host's own review as recorded on the chase, and reminders
  // actually sent from the portal (see client.ts's local schema note).
  hostReviewedAt?: string | null
  reminderSentCount?: number
  reminderDaysAgo?: number[]
  // Which list this row belongs in and its one next action, decided by
  // admin-api's chaseBucket so this page and the digest email agree. Pending
  // rows only; absent on checkout-today rows and during a rolling deploy.
  bucket?: 'do_today' | 'waiting'
  action?: 'review' | 'remind' | null
  // Why a waiting row is waiting, when the reason is the stay's own
  // recommendation rather than the calendar (CAP-443).
  reminderD7?: 'optional' | 'one_soft' | 'never' | null
}

// Airbnb sometimes anonymizes the guest name in its review-notification
// email (e.g. "A recent guest left a 3-star review"), so a chase's
// guestName can be null — fall back to a readable label instead of
// rendering nothing (or "undefined" in a template string).
function displayGuestName(guestName: string | null): string {
  return guestName || 'A guest'
}

const TIER_BADGE_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
  urgent: 'destructive',
  mild: 'warning',
  neutral: 'secondary',
}

function DaysBadge({ daysSince, tier }: { daysSince?: number | null; tier?: string }) {
  if (daysSince == null) return null
  return <Badge variant={(tier && TIER_BADGE_VARIANT[tier]) || 'secondary'}>{daysSince}d</Badge>
}

function SourceBadge({ platform }: { platform: string | null }) {
  if (!platform) return null
  const labels: Record<string, string> = { airbnb: 'Airbnb', vrbo: 'VRBO', direct: 'Direct' }
  const label = labels[platform.toLowerCase()] || platform
  // Pre-existing: Badge's cva config (components/ui/badge.tsx) has no
  // "outline" variant (only default/success/warning/destructive/secondary),
  // so this has always silently rendered with no variant styling applied at
  // all rather than an outlined look. Preserved as-is (cast, not corrected)
  // since picking the "right" variant is a design call outside a type-only
  // conversion — flagged in the migration report instead.
  return <Badge variant={'outline' as BadgeProps['variant']}>{label}</Badge>
}

interface SuggestionToggleButtonProps {
  isExpanded: boolean
  onToggle: () => void
}

function SuggestionToggleButton({ isExpanded, onToggle }: SuggestionToggleButtonProps) {
  const label = isExpanded ? 'Hide suggested review' : 'Show suggested review'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button size="sm" variant="outline" onClick={onToggle} aria-label={label}>
          <Sparkles className="h-3.5 w-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  )
}

interface ThankYouActions {
  clicked: boolean
  onReviewClick: () => void
  onOpenDialog: () => void
}

// Shown once the operator has clicked the "Review" link for this row —
// see thankYou.clicked's origin in DigestSection: there is no backend
// signal for "the host actually left the review yet" (Uplisting doesn't
// tell us when an externally-opened review link gets used), so this is a
// deliberately client-side workflow gate, not a source-of-truth reflection
// of backend state. It resets on reload — a fresh page load has no way to
// know whether a real review was left in a prior session either.
function ThankYouButton({ thankYou }: { thankYou: ThankYouActions | null }) {
  if (!thankYou?.clicked) return null
  return (
    <Button size="sm" variant="outline" onClick={thankYou.onOpenDialog}>
      Thank you
    </Button>
  )
}

interface RowActionsProps {
  row: DigestRow
  /**
   * The row's single next action, from admin-api's chaseBucket. Exactly one
   * control is promoted to the filled mint variant; everything else on the
   * row stays quiet. Null leaves every control secondary, which is what the
   * checking-out-today section wants.
   */
  primaryAction?: 'review' | 'remind' | null
  onSkip: (() => void) | null
  suggestionToggle: SuggestionToggleButtonProps | null
  thankYou: ThankYouActions | null
  /** Open the Remind dialog (pending rows with a chase only). */
  onRemind: (() => void) | null
  /** Record "I left a review" (VRBO has no feed for it; Airbnb fills in nightly). */
  onMarkReviewed: (() => void) | null
  /** See ReviewedMark's confirmWhenRecorded. */
  confirmReviewRecorded?: boolean
}

// The host's own review: only the outstanding case renders anything. A
// recorded review used to show a green "✓ You reviewed" on every row, which
// - because it was true of nearly every row - told the operator nothing
// while spending the one color that should mean "done". Which section the
// row is in now carries that, and the date stays available on the chase.
function ReviewedMark({
  row,
  onMarkReviewed,
  confirmWhenRecorded = false,
}: {
  row: DigestRow
  onMarkReviewed: (() => void) | null
  /**
   * Say so once the review is on the chase, instead of rendering nothing.
   *
   * The mark is irreversible and the control simply disappears when it lands,
   * which reads as a button that broke rather than as an action that worked -
   * reported as a bug on rows the operator had marked minutes earlier
   * (CAP-482). Only the checking-out-today section asks for this: there it is
   * the day's work and a handful of rows. On the pending lists a recorded
   * review is true of nearly every row, which is why the old green "You
   * reviewed" tick was removed from them in the first place.
   */
  confirmWhenRecorded?: boolean
}) {
  if (row.hostReviewedAt) {
    if (!confirmWhenRecorded) return null
    return (
      <span className="inline-flex items-center gap-1 text-xs text-textMuted">
        <Check className="h-3 w-3" aria-hidden="true" />
        Review recorded
      </span>
    )
  }
  if (!onMarkReviewed) return null
  return (
    <Button size="sm" variant="ghost" onClick={onMarkReviewed}>
      I left a review
    </Button>
  )
}

const REMINDER_DAY = 7

/**
 * Where a pending stay stands on its one day-7 reminder.
 *
 * Derived in one place and read everywhere, because the thing an operator
 * most needs to see - a guest still inside their review window who has never
 * been reminded - was previously only ever implied. It showed as a Remind
 * button in "Do today", so any row whose lead action was something else (the
 * host's own review outranks the reminder, and usually isn't recorded yet)
 * said nothing about the reminder at all, and a row parked in "Waiting" said
 * only "Nothing further worth sending" (CAP-482).
 *
 * `overdue` is the state worth acting on: past day 7, nothing sent, and the
 * stay's own recommendation allows one.
 */
type ReminderState = 'sent' | 'overdue' | 'scheduled' | 'not_applicable'

function reminderState(row: DigestRow): ReminderState {
  if ((row.reminderDaysAgo ?? []).length > 0) return 'sent'
  // 'never' is the platform or the tier closing the moment off: a VRBO stay
  // has a year to review rather than 14 days, a red-tier stay is not chased,
  // and a guest who reviewed first has nothing left to be reminded about.
  if (row.reminderD7 === 'never') return 'not_applicable'
  return (row.daysSince ?? 0) >= REMINDER_DAY ? 'overdue' : 'scheduled'
}

function reminderLabel(row: DigestRow): string {
  switch (reminderState(row)) {
    case 'sent':
      return `Reminded ${(row.reminderDaysAgo ?? []).map(d => `${d}d`).join(', ')} ago`
    case 'not_applicable':
      return 'No day-7 reminder for this stay'
    case 'overdue':
      return 'No day-7 reminder sent'
    default: {
      const until = REMINDER_DAY - (row.daysSince ?? 0)
      return until > 1 ? `Reminder due in ${until}d` : 'Reminder due tomorrow'
    }
  }
}

/**
 * The reminder state, rendered exactly once per pending row: as a chip in the
 * guest cell on an actionable row, and as the whole of the action cell on a
 * waiting one (which by definition has no control to sit beside). Rendering
 * it in both places put the same sentence on screen twice, which is what
 * CAP-403 had already had to undo once.
 */
function ReminderBadge({ row }: { row: DigestRow }) {
  const state = reminderState(row)
  if (state === 'not_applicable') return null
  const label = reminderLabel(row)
  if (state === 'overdue') {
    // A row whose promoted action is already the reminder says this with the
    // filled Remind button; an amber pill beside it is the same sentence
    // twice, and louder than the control it duplicates - which is the exact
    // shape of the pill CAP-440 had to tone down. The flag is for the rows
    // where the reminder is NOT the lead action, because the host's own
    // review outranks it, and which therefore said nothing about it at all.
    if (row.action === 'remind') return null
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber/40 bg-warningBg px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber">
        <BellOff className="h-2.5 w-2.5" aria-hidden="true" />
        {label}
      </span>
    )
  }
  return <span className="text-[11px] text-textMuted">{label}</span>
}

// What a "Waiting on the guest" row says instead of controls. Never a button
// - the whole point of the section is that there is nothing to press.
function WaitingStatus({ row }: { row: DigestRow }) {
  if (reminderState(row) === 'not_applicable') {
    return <span className="text-xs text-textMuted">{reminderLabel(row)}</span>
  }
  return <ReminderBadge row={row} />
}

function RemindButton({
  row,
  onRemind,
  primary = false,
}: {
  row: DigestRow
  onRemind: (() => void) | null
  primary?: boolean
}) {
  if (!onRemind) return null
  const sent = row.reminderSentCount ?? 0
  const reminded = row.reminderDaysAgo?.length ?? 0
  const history = reminded > 0 && (
    <span className="text-[11px] text-textMuted">
      reminded {(row.reminderDaysAgo ?? []).map(d => `${d}d`).join(', ')} ago
      {sent > 0 ? ` (${sent} sent from here)` : ''}
    </span>
  )

  return (
    <span className="inline-flex items-center gap-1.5">
      <Button size="sm" variant={primary ? 'default' : 'outline'} onClick={onRemind}>
        Remind
      </Button>
      {history}
    </span>
  )
}

function RowActions({
  row,
  primaryAction = null,
  onSkip,
  suggestionToggle,
  thankYou,
  onRemind,
  onMarkReviewed,
  confirmReviewRecorded = false,
}: RowActionsProps) {
  // Links were mint throughout, alongside mint ticks, mint ratings and a
  // mint confidence pill - so the color marked "good", "done" and "clickable"
  // at once and stopped distinguishing anything. A link is now ordinary text
  // unless it is this row's one action.
  const linkClass = 'inline-flex items-center gap-1 text-sm font-medium hover:underline'
  const reviewLinkClass =
    primaryAction === 'review'
      ? `${linkClass} rounded-button bg-mint px-3 py-1.5 text-charcoal hover:no-underline`
      : `${linkClass} text-text`
  if (row.guestReviewedFirst) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col items-start gap-1">
          {/*
            No rating yet is a fact about what the platform has shown us, not a
            problem with the stay - so it reads as muted prose rather than the
            alarm colour a missing value used to get ("X⭐️" in text-danger,
            CAP-446). Deliberately says only that the rating is not visible and
            not *why*: this branch renders both platforms, and the reasons
            differ. Airbnb is double-blind and withholds the guest's rating
            until the host reviews too, but VRBO publishes guest reviews
            independently, so "hidden until you review" would be wrong there.
          */}
          {row.guestRating ? (
            <span className="text-sm font-semibold text-danger">{row.guestRating}⭐️</span>
          ) : (
            <span className="text-sm text-textMuted">Rating not shown yet</span>
          )}
          {row.reviewLink && (
            <a
              href={row.reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={thankYou?.onReviewClick}
              className={reviewLinkClass}
            >
              Review
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <ReviewedMark
          row={row}
          onMarkReviewed={onMarkReviewed}
          confirmWhenRecorded={confirmReviewRecorded}
        />
        <ThankYouButton thankYou={thankYou} />
        {suggestionToggle && (
          <SuggestionToggleButton
            isExpanded={suggestionToggle.isExpanded}
            onToggle={suggestionToggle.onToggle}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {row.guestRating && (
        <span className="text-sm font-semibold text-mint">{row.guestRating}⭐️</span>
      )}
      {row.reviewLink && (
        <a
          href={row.reviewLink}
          target="_blank"
          rel="noopener noreferrer"
          onClick={thankYou?.onReviewClick}
          className={reviewLinkClass}
        >
          Review
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {row.messageUrl && (
        <a
          href={row.messageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${linkClass} text-text`}
        >
          Message
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
      <ReviewedMark
        row={row}
        onMarkReviewed={onMarkReviewed}
        confirmWhenRecorded={confirmReviewRecorded}
      />
      <ThankYouButton thankYou={thankYou} />
      <RemindButton row={row} onRemind={onRemind} primary={primaryAction === 'remind'} />
      {suggestionToggle && (
        <SuggestionToggleButton
          isExpanded={suggestionToggle.isExpanded}
          onToggle={suggestionToggle.onToggle}
        />
      )}
      {onSkip && (
        <Button size="sm" variant="outline" onClick={onSkip}>
          Skip
        </Button>
      )}
    </div>
  )
}

interface SuggestionRowProps {
  reservationId: string | null
  guestName: string | null
  platform: string | null
  reviewSuggestion: ReviewSuggestionEmbed
  collapsible: boolean
  isExpanded: boolean
}

// Whether a row's suggested-review block should render at all. For a
// collapsible section (Pending reviews — many rows, only checked
// occasionally, so an always-open block per row eats a lot of vertical
// space) it only renders once the host has explicitly asked to see it via
// the Sparkles toggle button in RowActions; a non-collapsible section
// (Checking out today — where the review is actually written same-day)
// always shows it inline.
function SuggestionRow({
  reservationId,
  guestName,
  platform,
  reviewSuggestion,
  collapsible,
  isExpanded,
}: SuggestionRowProps) {
  if (!reservationId || platform?.toLowerCase() !== 'airbnb') return null
  if (collapsible && !isExpanded) return null

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={3} className="pt-0 pb-3">
        <SuggestedReviewBlock
          reservationId={reservationId}
          guestName={guestName}
          initialSuggestion={reviewSuggestion}
        />
      </TableCell>
    </TableRow>
  )
}

interface DigestSectionProps<Row extends DigestRow> {
  title: string
  description?: string
  rows: Row[]
  showDaysBadge: boolean
  showSkip: boolean
  onSkip?: (row: Row) => void
  collapsibleSuggestion?: boolean
  showThankYou?: boolean
  /**
   * A section whose rows carry no action. The move has already been made and
   * the guest's response is what everything waits on, so the row states that
   * in words instead of offering the same six controls every other row has -
   * which is what made the old single table unreadable: every row looked
   * equally like something to do.
   */
  waiting?: boolean
  /** See ReviewedMark's confirmWhenRecorded. Checking-out-today only. */
  confirmReviewRecorded?: boolean
  /**
   * The stay a link (`?reservation=<id>`, from the property calendar) points at:
   * its row is marked and found by `data-reservation-row`. Nothing is opened.
   */
  focusReservationId?: string | null
}

function DigestSection<Row extends DigestRow>({
  title,
  description,
  rows,
  showDaysBadge,
  showSkip,
  onSkip,
  collapsibleSuggestion = false,
  showThankYou = false,
  waiting = false,
  confirmReviewRecorded = false,
  focusReservationId = null,
}: DigestSectionProps<Row>) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  // Separate from expandedIds above: the suggested-review panel and the
  // confidence-score panel are unrelated features that happen to share a
  // row, and there's no reason clicking one toggle should also open the
  // other.
  const [expandedConfidenceIds, setExpandedConfidenceIds] = useState<Set<string>>(new Set())
  // Which reservations the operator has clicked "Review" for this page
  // load — see ThankYouButton's doc comment for why this is client-side
  // only, not backend state.
  const [reviewClickedIds, setReviewClickedIds] = useState<Set<string>>(new Set())
  const [thankYouRow, setThankYouRow] = useState<Row | null>(null)
  const [remindTarget, setRemindTarget] = useState<RemindTarget | null>(null)
  // "I left a review" is irreversible on the backend (first writer wins,
  // nothing clears it), so it confirms first and disables while in flight.
  const [markTarget, setMarkTarget] = useState<DigestRow | null>(null)
  const [marking, setMarking] = useState(false)
  const sectionQueryClient = useQueryClient()
  const { toast: sectionToast } = useToast()
  const refetchDigest = () =>
    void sectionQueryClient.invalidateQueries({ queryKey: ['review-chase-digest'] })
  const confirmMarkReviewed = async () => {
    const row = markTarget
    if (!row?.platform || !row.externalId || marking) return
    setMarking(true)
    try {
      await api.markHostReviewed(row.platform, row.externalId)
      sectionToast({
        title: 'Recorded',
        description: `Your review of ${displayGuestName(row.guestName)} is on the chase.`,
      })
      setMarkTarget(null)
      refetchDigest()
    } catch (err) {
      const body = (err as { body?: { error?: string } }).body
      sectionToast({
        title: 'Could not record it',
        description: body?.error || 'The mark was not saved. Try again in a moment.',
        variant: 'destructive',
      })
    } finally {
      setMarking(false)
    }
  }

  const toggleExpanded = (reservationId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(reservationId)) {
        next.delete(reservationId)
      } else {
        next.add(reservationId)
      }
      return next
    })
  }

  const toggleConfidenceExpanded = (reservationId: string) => {
    setExpandedConfidenceIds(prev => {
      const next = new Set(prev)
      if (next.has(reservationId)) {
        next.delete(reservationId)
      } else {
        next.add(reservationId)
      }
      return next
    })
  }

  const markReviewClicked = (reservationId: string) => {
    setReviewClickedIds(prev => {
      if (prev.has(reservationId)) return prev
      const next = new Set(prev)
      next.add(reservationId)
      return next
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState message="Nothing here right now." />
        ) : (
          <div className="overflow-x-auto rounded-card border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => {
                  const key = `${row.platform ?? ''}:${row.externalId ?? row.guestName}:${i}`
                  const reservationId = row.reservationId
                  const isFocused = !!reservationId && reservationId === focusReservationId
                  const isSuggestionExpanded = reservationId
                    ? expandedIds.has(reservationId)
                    : false
                  const supportsSuggestedReviews = row.platform?.toLowerCase() === 'airbnb'
                  const willShowSuggestionRow =
                    supportsSuggestedReviews &&
                    !!reservationId &&
                    (!collapsibleSuggestion || isSuggestionExpanded)
                  const isConfidenceExpanded = reservationId
                    ? expandedConfidenceIds.has(reservationId)
                    : false
                  const willShowConfidenceRow = !!reservationId && isConfidenceExpanded
                  return (
                    <Fragment key={key}>
                      <TableRow
                        data-reservation-row={reservationId ?? undefined}
                        data-focused={isFocused ? 'true' : undefined}
                        className={cn(
                          (willShowSuggestionRow || willShowConfidenceRow) && 'border-b-0',
                          // A ring inside the row, so a table's overflow never clips it.
                          isFocused && 'bg-mint/10 ring-2 ring-inset ring-mint'
                        )}
                      >
                        <TableCell className="font-medium">
                          <span className="flex flex-wrap items-center gap-2">
                            {displayGuestName(row.guestName)}
                            <SourceBadge platform={row.platform} />
                            {showDaysBadge && (
                              <>
                                <DaysBadge daysSince={row.daysSince} tier={row.tier} />
                                {/* A waiting row states this in its action
                                    cell instead, where nothing else competes
                                    for the space. */}
                                {!waiting && <ReminderBadge row={row} />}
                              </>
                            )}
                            {reservationId && (
                              <ConfidenceToggle
                                reservationId={reservationId}
                                isExpanded={expandedConfidenceIds.has(reservationId)}
                                onToggle={() => toggleConfidenceExpanded(reservationId)}
                                // The digest already carries the latest summary; a
                                // row without one (older admin-api, never scored)
                                // is "no record", not "go and ask".
                                embedded={row.reviewConfidence ?? null}
                              />
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="text-textMuted">{row.propertyLabel}</TableCell>
                        <TableCell>
                          {waiting ? (
                            <WaitingStatus row={row} />
                          ) : (
                            <RowActions
                              row={row}
                              primaryAction={row.action ?? null}
                              onSkip={showSkip && onSkip ? () => onSkip(row) : null}
                              suggestionToggle={
                                supportsSuggestedReviews && reservationId && collapsibleSuggestion
                                  ? {
                                      isExpanded: isSuggestionExpanded,
                                      onToggle: () => toggleExpanded(reservationId),
                                    }
                                  : null
                              }
                              thankYou={
                                showThankYou && reservationId
                                  ? {
                                      // A recorded host review counts as "you clicked
                                      // Review": the thank-you follows either way.
                                      clicked:
                                        reviewClickedIds.has(reservationId) || !!row.hostReviewedAt,
                                      onReviewClick: () => markReviewClicked(reservationId),
                                      onOpenDialog: () => setThankYouRow(row),
                                    }
                                  : null
                              }
                              onRemind={
                                showSkip && row.platform && row.externalId
                                  ? () =>
                                      setRemindTarget({
                                        platform: row.platform as string,
                                        externalId: row.externalId as string,
                                        guestName: row.guestName ?? null,
                                      })
                                  : null
                              }
                              onMarkReviewed={
                                row.platform && row.externalId ? () => setMarkTarget(row) : null
                              }
                              confirmReviewRecorded={confirmReviewRecorded}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                      <SuggestionRow
                        reservationId={reservationId}
                        guestName={row.guestName}
                        platform={row.platform}
                        reviewSuggestion={row.reviewSuggestion}
                        collapsible={collapsibleSuggestion}
                        isExpanded={isSuggestionExpanded}
                      />
                      <ConfidenceRow
                        reservationId={reservationId}
                        isExpanded={isConfidenceExpanded}
                      />
                    </Fragment>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <RemindDialog
        target={remindTarget}
        onOpenChange={open => {
          if (!open) setRemindTarget(null)
        }}
        onSent={refetchDigest}
      />
      <Dialog
        open={!!markTarget}
        onOpenChange={open => {
          if (!open && !marking) setMarkTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Record your review of {markTarget && displayGuestName(markTarget.guestName)}?
            </DialogTitle>
            <DialogDescription>
              This marks the chase as reviewed by you today and cannot be undone. Only for a review
              you have already submitted on the platform.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkTarget(null)} disabled={marking}>
              Cancel
            </Button>
            <Button onClick={() => void confirmMarkReviewed()} disabled={marking}>
              {marking ? 'Recording…' : 'Yes, I left a review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showThankYou && (
        <ThankYouMessageDialog
          reservationId={thankYouRow?.reservationId}
          guestName={thankYouRow?.guestName}
          onSent={refetchDigest}
          open={!!thankYouRow}
          onOpenChange={(open: boolean) => {
            if (!open) setThankYouRow(null)
          }}
        />
      )}
    </Card>
  )
}

function SkippedSection({ rows }: { rows: ReviewChaseSkippedRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Skipped</CardTitle>
        <CardDescription>Guests the host chose not to chase for a review.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <EmptyState message="No skipped chases in the current window." />
        ) : (
          <div className="overflow-x-auto rounded-card border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={`${row.platform ?? ''}:${row.externalId ?? row.guestName}:${i}`}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        {displayGuestName(row.guestName)}
                        <SourceBadge platform={row.platform} />
                      </span>
                    </TableCell>
                    <TableCell className="text-textMuted">{row.propertyLabel}</TableCell>
                    <TableCell className="text-textMuted">
                      {row.skipReason || 'No reason given'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * `embedded` renders the digest sections inside the Reviews page's "Act
 * today" card (no page-level spacing of its own); the bare route
 * /review-chase now redirects there.
 */
export function ReviewChasePage({ embedded = false }: { embedded?: boolean } = {}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showSkipped, setShowSkipped] = useState(false)
  const [skipTarget, setSkipTarget] = useState<ReviewChasePendingRow | null>(null)
  const skipForm = useForm<SkipReasonValues>({
    resolver: zodResolver(skipReasonSchema),
    defaultValues: { text: '' },
  })
  const [isSkipping, setIsSkipping] = useState(false)
  const [searchParams] = useSearchParams()
  const focusReservationId = searchParams.get(REVIEW_ROW_PARAM)?.trim() || null

  const query = useQuery({
    queryKey: ['review-chase-digest'],
    queryFn: () => api.getReviewChaseDigest(),
  })

  // A row that arrives without a bucket (an admin-api still mid-deploy) is
  // treated as work rather than hidden: showing something that needs no
  // action costs a glance, silently dropping something that does costs a
  // review.
  const pendingRows = query.data?.pendingRows ?? []
  const waitingRows = pendingRows.filter(r => r.bucket === 'waiting')
  const doTodayRows = pendingRows.filter(r => r.bucket !== 'waiting')
  // Counted across both lists, not just "Do today": a stay past day 7 with no
  // reminder is the same outstanding work whether or not the row's own lead
  // action happens to be the reminder (CAP-482).
  const unremindedCount = pendingRows.filter(r => reminderState(r) === 'overdue').length

  // The stay a link points at: scrolled to once its row has landed, then left
  // alone (a refresh must not pull the page back). The row is marked either way.
  const focusRow = focusReservationId
    ? [...(query.data?.checkoutTodayRows ?? []), ...pendingRows].find(
        row => row.reservationId === focusReservationId
      )
    : undefined
  const scrolledFor = useRef<string | null>(null)
  useEffect(() => {
    if (!focusReservationId || !focusRow || scrolledFor.current === focusReservationId) return
    const target = Array.from(document.querySelectorAll('[data-reservation-row]')).find(
      el => el.getAttribute('data-reservation-row') === focusReservationId
    )
    if (!target) return
    scrolledFor.current = focusReservationId
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView?.({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
  }, [focusReservationId, focusRow])

  const openSkipDialog = (row: ReviewChasePendingRow) => {
    setSkipTarget(row)
    skipForm.reset({ text: '' })
  }

  const closeSkipDialog = () => {
    setSkipTarget(null)
    skipForm.reset({ text: '' })
  }

  const onSubmitSkip = async (values: SkipReasonValues) => {
    if (!skipTarget) return
    setIsSkipping(true)
    try {
      await api.skipReviewChase({
        platform: skipTarget.platform,
        externalId: skipTarget.externalId,
        reason: values.text.trim() || undefined,
      })
      await queryClient.invalidateQueries({ queryKey: ['review-chase-digest'] })
      toast({
        title: 'Chase skipped',
        description: `${displayGuestName(skipTarget.guestName)} won't be chased for a review.`,
      })
      closeSkipDialog()
    } catch (error: unknown) {
      toast({
        title: 'Failed to skip',
        description: error instanceof Error ? error.message : 'Something went wrong.',
        variant: 'destructive',
      })
    } finally {
      setIsSkipping(false)
    }
  }

  // skipForm.handleSubmit(onSubmitSkip) reads internal refs, so it's
  // wrapped here and invoked from this click handler rather than called
  // inline during render (same pattern as composer.tsx/ask-tab.tsx) —
  // there's no <form> element here (matching the original plain-div dialog
  // body), so this is the only place the actual submission is triggered
  // from.
  const handleConfirmSkipClick = () => {
    void skipForm.handleSubmit(onSubmitSkip)()
  }

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            variant={showSkipped ? 'default' : 'outline'}
            onClick={() => setShowSkipped(prev => !prev)}
          >
            {showSkipped ? 'Hide skipped' : 'Show skipped'}
          </Button>
          {unremindedCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber/40 bg-warningBg px-2.5 py-1 text-xs font-medium text-amber">
              <BellOff className="h-3 w-3" aria-hidden="true" />
              {unremindedCount === 1
                ? '1 guest is past day 7 with no reminder sent'
                : `${unremindedCount} guests are past day 7 with no reminder sent`}
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {query.isLoading ? (
        <LoadingSkeleton className="h-64 w-full" />
      ) : !query.data ? (
        // Only render the full-page error when we've never loaded data at
        // all. A failed manual refresh or post-skip refetch still leaves
        // query.error set even though TanStack Query keeps the last
        // successful query.data around — replacing the whole page with an
        // error in that case would hide the still-valid last known digest
        // behind a transient failure.
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          {focusReservationId && (
            <p
              role="status"
              data-testid="review-row-focus-note"
              className="rounded-card border border-border px-4 py-2 text-sm text-textMuted"
            >
              {focusRow
                ? `Showing ${displayGuestName(focusRow.guestName)}'s row, from the calendar. Nothing has been opened or sent.`
                : `Reservation ${focusReservationId} is not in today's chase. It may already be reviewed or skipped, or its window may have closed.`}
            </p>
          )}
          {query.error && (
            <div className="rounded-card border border-danger/30 bg-danger/10 px-4 py-2 text-sm text-danger">
              Refresh failed ({query.error.message || 'unknown error'}) — showing the last loaded
              data.
            </div>
          )}
          <DigestSection
            title="Checking out today"
            description="Best moment to message a guest and request a review."
            rows={query.data.checkoutTodayRows}
            showDaysBadge={false}
            showSkip={false}
            showThankYou
            confirmReviewRecorded
            focusReservationId={focusReservationId}
          />
          {doTodayRows.length > 0 && (
            <DigestSection
              title="Do today"
              description="One thing to do on each, most overdue first."
              rows={doTodayRows}
              showDaysBadge
              showSkip
              onSkip={openSkipDialog}
              collapsibleSuggestion
              showThankYou
              focusReservationId={focusReservationId}
            />
          )}
          {waitingRows.length > 0 && (
            <DigestSection
              title="Waiting on the guest"
              description="Your move is made. Each row says where its day-7 reminder stands."
              rows={waitingRows}
              showDaysBadge
              showSkip={false}
              collapsibleSuggestion
              waiting
              focusReservationId={focusReservationId}
            />
          )}
          {doTodayRows.length === 0 && waitingRows.length === 0 && (
            <p className="rounded-card border border-border px-4 py-3 text-sm text-textMuted">
              No one is waiting on a review right now.
            </p>
          )}
          {showSkipped && <SkippedSection rows={query.data.skippedRows} />}
        </>
      )}

      <Dialog
        open={!!skipTarget}
        onOpenChange={open => {
          // Guard every dismissal path (X button, Escape, outside click) —
          // not just the footer buttons, which are already disabled — so a
          // skip in flight for guest A can't be dismissed to open the dialog
          // for guest B; without this, A's request completing afterward
          // would unconditionally close whatever dialog is open at that
          // point (B's) and discard B's entered reason.
          if (!open && !isSkipping) closeSkipDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skip {skipTarget && displayGuestName(skipTarget.guestName)}?</DialogTitle>
            <DialogDescription>
              They won&apos;t be chased for a review again. This can&apos;t be undone.
            </DialogDescription>
          </DialogHeader>
          <Form {...skipForm}>
            <div className="py-2">
              <FormField
                control={skipForm.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <textarea
                        {...field}
                        placeholder="Optional: why are you skipping this guest?"
                        className="w-full min-h-[100px] rounded-input border border-border bg-surface2 px-3 py-2 text-sm text-text transition-all placeholder:text-textFaint focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                        disabled={isSkipping}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeSkipDialog} disabled={isSkipping}>
                Cancel
              </Button>
              <Button onClick={handleConfirmSkipClick} disabled={isSkipping}>
                {isSkipping ? 'Skipping...' : 'Confirm skip'}
              </Button>
            </DialogFooter>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
